/**
 * Copyright (c) Nicolas Gallagher.
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { StyleWrapper, Value } from "./types"

const emptyObject = {}
const objects = {}
const prefix = "r"
let uniqueID = 1

const createKey = (id: string | number) => `${prefix}-${id}`

export default class ReactNativePropRegistry {
  static register<T extends Object>(object: T): StyleWrapper<T> {
    const id = uniqueID++
    const key = createKey(id)
    const styleObject: StyleWrapper<T> = {
      ...object,
      __styleId: id,
    }
    if (process.env.NODE_ENV !== "production") {
      Object.freeze(object)
    }
    objects[key] = object
    return styleObject
  }

  static getByID(id: number): Object {
    if (!id) {
      // Used in the style={[condition && id]} pattern,
      // we want it to be a no-op when the value is false or null
      return emptyObject
    }
    const key = createKey(id)
    const object = objects[key]
    if (!object) {
      console.warn(`Invalid style with id \`${id}\`. Skipping ...`)
      return emptyObject
    }
    return object
  }

  static isStyleWrapper(style: Value): boolean {
    return typeof style === "object" && typeof style["__styleId"] === "number"
  }

  static getStyle(style: Value): object | undefined {
    if (ReactNativePropRegistry.isStyleWrapper(style)) {
      return ReactNativePropRegistry.getByID(
        (style as StyleWrapper<typeof style>).__styleId,
      )
    }
    return style
  }
}
